import React, { useEffect, useRef, useState } from "react";
import KPITile from "../../../../../common/KPITile/KPITile";
import KPIDetailTile from "../../../../../common/KPIDetailTile/KPIDetailTile";
import Benchmark from "../../../../../common/Benchmark/Benchmark";
import VarianceValue from "../../../../../common/VarianceValue/VarianceValue";
import styles from "./RenewalRateTile.module.scss";
import { useTranslation } from "react-i18next";
import { useProfileParams } from "../../../../../../utils/common";
import { useGetBusinessPerformanceDetailsQuery } from "../../../../../../redux/services/businessPerformaceAPI";
import ViewModeSwitcher, {
  ViewMode,
} from "../../../../../common/ViewModeSwitcher/ViewModeSwitcher";
import GraphCard from "../../../../../common/GraphCard/GraphCard";
import GenericTable from "../../../../../common/GenericTable/GenericTable";
import InsightAI from "../../../../../common/InsightAI/InsightAI";
import Skeleton from "../../../../../common/Skeleton/Skeleton";
import isEmpty from "lodash/isEmpty";
import { useGetAGCAIDetailsMutation } from "../../../../../../redux/services/agcAIAPI";
import { isValidLineChartData } from "../../../../../../utils/common";

interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

export const RenewalRateTile: React.FC<SummaryProp> = ({
  summary,
  loading = false,
}) => {
  const valueNum = typeof summary?.kpiValue === "number" ? summary.kpiValue : 0;
  const benchmark = summary?.benchmark;
  const { t } = useTranslation();
  const comparisonCriteria = summary?.kpiVarianceComparisonCriteria ?? "yoy";
  return (
    <KPITile
      title={t("renewalRate.title")}
      dataTestId="renewal-rate-tile"
      loading={loading}
    >
      <div className={styles.tile_content}>
        <div className={styles.values_wrapper}>
          <div className={styles.value_wrapper}>
            <div className={styles.value_content}>{valueNum}%</div>
            <VarianceValue
              value={summary?.kpiVarianceValue ?? 0}
              dataType={summary?.kpiVarianceDataType ?? "percentage"}
              currency={summary?.kpiCurrency}
              comparisonCriteria={comparisonCriteria}
            />
          </div>
        </div>
        <div className={styles.bar_wrapper}>
          <Benchmark
            minValue={benchmark?.min}
            maxValue={benchmark?.max}
            averageValue={benchmark?.mid}
            medianValue={benchmark?.median}
            quartileLine={[benchmark?.lowerQuartile ?? 0, benchmark?.upperQuartile ?? 0]}
            currentBenchmarkValue={valueNum}
            benchmarkDataType={summary?.benchmarkDataType}
            isPercentValue={false}
          />
        </div>
      </div>
    </KPITile>
  );
};

export const RenewalRateDetail: React.FC<{ onClose?: () => void }> = ({
  onClose,
}) => {
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>("left");
  const kpiCode = "renewalRate010";

  const { data: detailsResponse, isLoading } =
    useGetBusinessPerformanceDetailsQuery(
      {
        affiliateCode,
        aboNumber,
        payload: { kpis: [{ code: kpiCode }] } as any,
      },
      { skip: !onClose }
    );

  const [getAGCAIDetails, { isLoading: isLoadingAiSummary }] = useGetAGCAIDetailsMutation();
  const [aiContent, setAiContent] = useState<string>("");
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    // Reset ref when modal closes
    if (!onClose) {
      hasRequestedRef.current = false;
      setAiContent("");
      return;
    }

    const canFetch =
      Number(affiliateCode) !== 0 && aboNumber !== 0 && Boolean(onClose);
    if (!canFetch || hasRequestedRef.current) return;
    
    hasRequestedRef.current = true;
    getAGCAIDetails({
      affiliateCode,
      aboNumber,
      payload: { kpis: [{ code: kpiCode }] } as any,
    })
      .then((resp: any) => {
        // Handle different response structures
        const responseData = resp?.data || resp;
        const aiRaw = responseData?.body || responseData;
        
        // Try multiple extraction patterns
        const aiResolved =
          typeof aiRaw === "string"
            ? aiRaw
            : aiRaw?.[kpiCode] || aiRaw?.summary || aiRaw || "";
        
        const content = aiResolved
          ? String(aiResolved).slice(0, 300).concat("...")
          : "";
        setAiContent(content);
      })
      .catch((error: any) => {
        console.error("Error fetching AI details:", error);
        setAiContent("");
      });
  }, [affiliateCode, aboNumber, onClose, kpiCode, getAGCAIDetails]);

  const detailData = detailsResponse?.body?.kpis?.[0] || {};
  const monthlyViewCharts = detailData?.detail?.monthlyView?.chart || [];

  // Extract line chart data
  const lineChartData = monthlyViewCharts.find(
    (chart: any) => chart.chartType === "lineChart"
  );
  const lineChartDetailData = lineChartData?.chartDetail?.lineChartDetail;
  
  // Validate lineChartData has valid structure with lines and yAxis
  const hasValidLineChartData = isValidLineChartData(lineChartDetailData);
  const hasChartData = hasValidLineChartData;

  // Extract table data
  const tableChart = monthlyViewCharts.find(
    (chart: any) => chart.chartType === "table"
  );
  const tableData = tableChart?.chartDetail?.tableDetail;
  const currency = detailData?.kpiCurrency || "USD";

  useEffect(() => {
    if (!hasChartData) {
      setViewMode("right");
    }
  }, [hasChartData]);

  useEffect(() => {
    if (lineChartDetailData !== undefined && isEmpty(lineChartDetailData)) {
      setViewMode("right");
    }
  }, [lineChartDetailData]);
  

  if (isLoading) return <Skeleton height="400px" />;

  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>{t("renewalRate.title")}</div>
        <div className={styles.desc}>{t("renewalRate.description")}</div>
      </div>
      {hasChartData && (
        <div className={styles.chart_view_wrapper}>
          <ViewModeSwitcher
            mode={viewMode}
            onChangeMode={setViewMode}
            disabled={false}
          />
        </div>
      )}
    </div>
  );

  const renderTable = () =>
    isEmpty(tableData) ? (
      <div className={styles.no_data}>{t("noDataAvailable")}</div>
    ) : (
      <GenericTable
        tableData={tableData}
        kpiCode={kpiCode}
        highlightColumns={[1]}
      />
    );

  const leftContent =
    hasChartData && viewMode === "left" ? (
      <GraphCard
        lineChartData={hasValidLineChartData ? lineChartDetailData : undefined}
        currency={currency}
        chartSummaryData={lineChartData?.chartSummary}
      />
    ) : (
      renderTable()
    );

  const rightContent = isLoadingAiSummary ? (
    <Skeleton height="200px" />
  ) : (
    <InsightAI
      updateDate=""
      content={aiContent}
      title={t("ai_summary")}
      showDropdown={false}
      isDropdownEnabled={true}
      flowerIconSize={24}
      titleSize={14}
    />
  );
  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="renewal-rate-detail"
    />
  );
};

export default RenewalRateTile;
